import { useState, useEffect, useCallback } from 'react';
import { Search, UploadCloud, Download, Layers, Building2, RefreshCw, Plus } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { SpellersImportModal } from '../components/Spellers/SpellersImportModal';
import { SpellersProjectsList } from '../components/Spellers/SpellersProjectsList';
import { SpellersContractorsList } from '../components/Spellers/SpellersContractorsList';
import { SpellersLinkCrmModal } from '../components/Spellers/SpellersLinkCrmModal';
import { SpellersNewProjectModal } from '../components/Spellers/SpellersNewProjectModal';
import { SpellersUnmatchedSection } from '../components/Spellers/SpellersUnmatchedSection';
import { ContactNotesTasksModal } from '../components/ContactNotesTasksModal';
import api from '../services/api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpellersPage({ user, onLogout }) {
  const [tab, setTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [unmatchedProjects, setUnmatchedProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [linkCrmAccount, setLinkCrmAccount] = useState(null);
  const [modalAccount, setModalAccount] = useState(null);
  const [syncingPackages, setSyncingPackages] = useState(false);
  const [crmSyncRunning, setCrmSyncRunning] = useState(false); // Contractors' contact + latest note are refreshing from the CRM

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_unmatched: true };
      if (q) params.q = q;
      if (stageFilter) params.stage = stageFilter;
      if (statusFilter) params.project_status = statusFilter;
      const res = await api.get(`${API}/spellers/projects`, { params });
      const all = res.data.projects || [];
      setProjects(all.filter(p => p.crm_matched));
      setUnmatchedProjects(all.filter(p => !p.crm_matched));
    } catch (e) {
      toast.error('Failed to load Spellers projects');
    } finally {
      setLoading(false);
    }
  }, [q, stageFilter, statusFilter]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const res = await api.get(`${API}/spellers/accounts`, { params });
      setAccounts(res.data.accounts || []);
    } catch (e) {
      toast.error('Failed to load Contractors');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (tab === 'projects') fetchProjects();
    else fetchAccounts();
  }, [tab, fetchProjects, fetchAccounts]);

  // Contractors' contact and latest note come from the CRM and refresh in the background: while that is running, re-read the list
  // every few seconds (quietly, no spinner) so they appear as they arrive.
  useEffect(() => {
    if (tab !== 'contractors' || !crmSyncRunning) return undefined;
    let cancelled = false;
    let polls = 0;
    const timer = setInterval(async () => {
      polls += 1;
      try {
        const res = await api.get(`${API}/spellers/accounts`, { params: q ? { q } : {} });
        if (cancelled) return;
        setAccounts(res.data.accounts || []);
        if (!res.data.crm_sync?.running || polls >= 15) setCrmSyncRunning(false);
      } catch (_) {
        if (!cancelled) setCrmSyncRunning(false);
      }
    }, 4000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [tab, crmSyncRunning, q]);

  const stages = Array.from(new Set(projects.map(p => p.stage).filter(Boolean)));
  const statuses = Array.from(new Set(projects.map(p => p.project_status).filter(Boolean)));

  const handleProjectUpdated = (updated) => {
    setProjects(prev => prev.map(p => (p.id === updated.id ? updated : p)));
  };
  const handleAccountUpdated = (updated) => {
    setAccounts(prev => prev.map(a => (a.id === updated.id ? updated : a)));
  };

  const handleLinked = async (zohoAccount) => {
    if (!linkCrmAccount) return;
    try {
      const res = await api.put(`${API}/spellers/accounts/${linkCrmAccount.id}/link-crm`, { zoho_account_id: zohoAccount.id });
      handleAccountUpdated(res.data.account);
      toast.success('Linked to CRM');
      setLinkCrmAccount(null);
      setModalAccount({ id: zohoAccount.id, name: zohoAccount.name });
    } catch (e) {
      toast.error('Failed to link account');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`${API}/spellers/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Speller_Metcalfe_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleSyncPackages = async () => {
    setSyncingPackages(true);
    try {
      const res = await api.post(`${API}/spellers/projects/sync-packages-bulk`);
      toast.success(res.data.message || 'Refreshed from CRM');
      fetchProjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setSyncingPackages(false);
    }
  };

  const handleSyncContractors = async () => {
    try {
      await api.post(`${API}/spellers/accounts/sync-crm`);
      toast.success('Refreshing contacts and notes from the CRM…');
      setCrmSyncRunning(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    }
  };

  const handleProjectCreated = (created) => {
    setProjects(prev => [created, ...prev]);
    setShowNewProject(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        onLogout={onLogout}
        scrollWithPage
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <Layers className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Spellers</span>
          </div>
        }
        subHeaderRight={
          <div className="flex items-center gap-1.5 flex-nowrap">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap flex-shrink-0"
              style={{ backgroundColor: '#1f3a5f' }}
              data-testid="spellers-export-btn"
            >
              <Download className="w-3 h-3" /> {exporting ? 'Exporting…' : 'Export'}
            </button>
            {isSuperAdmin && (
              <button
                onClick={handleSyncPackages}
                disabled={syncingPackages}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap flex-shrink-0"
                style={{ backgroundColor: '#0f766e' }}
                data-testid="spellers-sync-packages-btn"
                title="Refresh live from CRM"
              >
                <RefreshCw className={`w-3 h-3 ${syncingPackages ? 'animate-spin' : ''}`} /> {syncingPackages ? 'Syncing…' : 'Sync'}
              </button>
            )}
            <button
              onClick={() => setShowNewProject(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap flex-shrink-0"
              style={{ backgroundColor: '#b45309' }}
              data-testid="spellers-new-project-btn"
              title="New Project"
            >
              <Plus className="w-3 h-3" /> New
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => setShowImport(true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap flex-shrink-0"
                style={{ backgroundColor: '#ee7203' }}
                data-testid="spellers-open-import-btn"
                title="Import Data"
              >
                <UploadCloud className="w-3 h-3" /> Import
              </button>
            )}
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6" data-testid="spellers-page">
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Spellers</h1>
            <p className="text-sm text-gray-500">Speller Metcalfe targeting tracker — projects, packages & contacts</p>
          </div>
          <div className="flex justify-start lg:justify-center">
            <div className="relative w-full max-w-xs">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tab === 'projects' ? 'Search project or postcode…' : 'Search company or contact…'}
                className="w-full pl-8 pr-3 py-2 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="spellers-search-input"
              />
            </div>
          </div>
          {tab === 'projects' ? (
            <div className="flex justify-start lg:justify-end gap-2">
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="px-3 py-2 rounded-full border border-gray-200 text-sm bg-white"
                data-testid="spellers-stage-filter"
              >
                <option value="">All stages</option>
                {stages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-full border border-gray-200 text-sm bg-white"
                data-testid="spellers-status-filter"
              >
                <option value="">All statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ) : <div />}
        </div>

        <div className="flex items-center gap-2 mb-4" data-testid="spellers-tabs">
          <button
            onClick={() => setTab('projects')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'projects' ? 'text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            style={tab === 'projects' ? { backgroundColor: '#ee7203' } : {}}
            data-testid="spellers-tab-projects"
          >
            <Layers className="w-3.5 h-3.5" /> Projects
          </button>
          <button
            onClick={() => setTab('contractors')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'contractors' ? 'text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            style={tab === 'contractors' ? { backgroundColor: '#ee7203' } : {}}
            data-testid="spellers-tab-contractors"
          >
            <Building2 className="w-3.5 h-3.5" /> Contractors
          </button>
          <button
            onClick={handleSyncContractors}
            disabled={crmSyncRunning}
            className={`${tab === 'contractors' ? 'flex' : 'hidden'} items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-white text-gray-600 border border-gray-200 disabled:opacity-60`}
            data-testid="spellers-refresh-contractors-btn"
            title="Pull the latest contact and newest note for every contractor from the CRM"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${crmSyncRunning ? 'animate-spin' : ''}`} /> {crmSyncRunning ? 'Refreshing from CRM…' : 'Refresh from CRM'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Loading…</div>
        ) : tab === 'projects' ? (
          projects.length === 0 && unmatchedProjects.length === 0 && isSuperAdmin ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
              <Layers className="w-10 h-10 text-gray-300" />
              <p className="text-gray-600 font-medium">No Spellers projects yet</p>
              <p className="text-gray-400 text-sm">Click "New" to create one, or "Import Data" to load the tracker spreadsheet</p>
            </div>
          ) : (
            <>
              <SpellersProjectsList projects={projects} onUpdated={handleProjectUpdated} user={user} />
              <SpellersUnmatchedSection projects={unmatchedProjects} />
            </>
          )
        ) : (
          <SpellersContractorsList
            accounts={accounts}
            onUpdated={handleAccountUpdated}
            onOpenAccount={(a) => setModalAccount({ id: a.zoho_account_id, name: a.company })}
            onLinkCrm={(a) => setLinkCrmAccount(a)}
          />
        )}
      </div>

      {showImport && (
        <SpellersImportModal
          isOpen={showImport}
          onClose={() => setShowImport(false)}
          onImported={() => { fetchProjects(); fetchAccounts(); }}
        />
      )}

      {showNewProject && (
        <SpellersNewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreated={handleProjectCreated}
        />
      )}

      {linkCrmAccount && (
        <SpellersLinkCrmModal
          account={linkCrmAccount}
          onClose={() => setLinkCrmAccount(null)}
          onLinked={handleLinked}
        />
      )}

      {modalAccount && (
        <ContactNotesTasksModal
          isOpen
          onClose={() => setModalAccount(null)}
          initialAccount={modalAccount}
        />
      )}
    </div>
  );
}

